import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Eye, X, Loader2, Inbox, ShoppingBag, Truck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { spiceLabel } from "../../lib/spice";

const STATUS_STYLES = {
  New: "bg-nosh-gold/15 text-nosh-gold border-nosh-gold/40",
  Contacted: "bg-blue-50 text-blue-700 border-blue-200",
  Booked: "bg-nosh-green/10 text-nosh-green border-nosh-green/40",
  Closed: "bg-nosh-ink/5 text-nosh-ink/50 border-nosh-border",
};
const STATUSES = ["New", "Contacted", "Booked", "Closed"];

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-nosh-border/60 pb-2">
    <span className="font-poster text-[11px] font-bold uppercase tracking-wide text-nosh-ink/45">{label}</span>
    <span className="text-right text-nosh-ink/80">{value}</span>
  </div>
);

export default function InquiriesTab() {
  const { API, authHeaders, logout } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const onErr = useCallback((e) => { if (e.response?.status === 401) { toast.error("Session expired"); logout(); } }, [logout]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, { headers: authHeaders });
      setInquiries(data);
    } catch (e) { onErr(e); } finally { setLoading(false); }
  }, [API, authHeaders, onErr]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}`, { status }, { headers: authHeaders });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}`);
    } catch (e) { onErr(e); toast.error("Could not update status"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: authHeaders });
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      setDetail(null);
      toast.success("Inquiry deleted");
    } catch (e) { onErr(e); toast.error("Could not delete"); }
  };

  if (loading) return <div className="grid place-items-center rounded-2xl border border-nosh-border bg-white py-20"><Loader2 className="h-6 w-6 animate-spin text-nosh-green" /></div>;
  if (!inquiries.length) return (
    <div className="grid place-items-center rounded-2xl border border-nosh-border bg-white py-20 text-center">
      <Inbox className="h-8 w-8 text-nosh-ink/30" />
      <p className="mt-3 font-body text-nosh-ink/55">No inquiries yet. They'll show up here as customers submit requests.</p>
    </div>
  );

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-nosh-border bg-white">
        <div className="hidden grid-cols-[1.4fr_1.2fr_0.8fr_0.7fr_1fr_0.7fr] gap-3 border-b border-nosh-border bg-nosh-surface px-5 py-3 font-poster text-[10px] font-bold uppercase tracking-widest text-nosh-ink/50 lg:grid">
          <span>Guest</span><span>Event</span><span>Fulfillment</span><span>Total</span><span>Status</span><span className="text-right">Actions</span>
        </div>
        {inquiries.map((inq) => (
          <div key={inq.id} data-testid={`inquiry-row-${inq.id}`} className="grid grid-cols-1 gap-3 border-b border-nosh-border/60 px-5 py-4 last:border-0 lg:grid-cols-[1.4fr_1.2fr_0.8fr_0.7fr_1fr_0.7fr] lg:items-center">
            <div className="min-w-0">
              <div className="font-display text-base font-bold text-nosh-ink">{inq.name}</div>
              <div className="truncate font-body text-xs text-nosh-ink/55">{inq.phone} · {inq.email}</div>
            </div>
            <div className="font-body text-sm text-nosh-ink/70">
              <div>{inq.event_date || "No date"}</div>
              <div className="text-xs text-nosh-ink/45">{inq.guests ? `${inq.guests} guests` : "—"} · {inq.event_type || "—"}</div>
            </div>
            <div>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-poster text-[10px] font-bold uppercase tracking-wide ${inq.fulfillment === "Delivery" ? "bg-blue-50 text-blue-700" : "bg-nosh-green/10 text-nosh-green"}`}>
                {inq.fulfillment === "Delivery" ? <Truck className="h-3 w-3" /> : <ShoppingBag className="h-3 w-3" />}{inq.fulfillment}
              </span>
            </div>
            <div className="font-display text-lg font-bold text-nosh-green">${inq.estimated_total}</div>
            <div>
              <select data-testid={`status-select-${inq.id}`} value={inq.status || "New"} onChange={(e) => updateStatus(inq.id, e.target.value)} className={`w-full rounded-full border px-3 py-1.5 font-poster text-[11px] font-bold uppercase tracking-wide outline-none ${STATUS_STYLES[inq.status || "New"]}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 lg:justify-end">
              <button onClick={() => setDetail(inq)} data-testid={`view-inquiry-${inq.id}`} className="grid h-9 w-9 place-items-center rounded-full border border-nosh-border text-nosh-ink/60 transition-colors hover:border-nosh-green hover:text-nosh-green"><Eye className="h-4 w-4" /></button>
              <button onClick={() => remove(inq.id)} data-testid={`delete-inquiry-${inq.id}`} className="grid h-9 w-9 place-items-center rounded-full border border-nosh-border text-nosh-ink/60 transition-colors hover:border-red-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {detail && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDetail(null)} className="fixed inset-0 z-40 bg-nosh-ink/60" />
            <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 20 }} className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[1.5rem] border border-nosh-border bg-nosh-cream p-6 shadow-2xl" data-testid="inquiry-detail-modal">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-poster text-[10px] font-bold uppercase tracking-widest text-nosh-gold">{detail.status}</div>
                  <h3 className="font-display text-2xl font-bold text-nosh-ink">{detail.name}</h3>
                </div>
                <button onClick={() => setDetail(null)} className="grid h-9 w-9 place-items-center rounded-full bg-nosh-surface text-nosh-ink"><X className="h-5 w-5" /></button>
              </div>
              <div className="mt-4 grid gap-2 font-body text-sm">
                <Row label="Phone" value={detail.phone} />
                <Row label="Email" value={detail.email} />
                <Row label="Event date" value={detail.event_date || "—"} />
                <Row label="Guests" value={detail.guests || "—"} />
                <Row label="Occasion" value={detail.event_type || "—"} />
                <Row label="Fulfillment" value={detail.fulfillment} />
                {detail.fulfillment === "Delivery" && <Row label="Address" value={detail.delivery_address || "—"} />}
              </div>
              <h4 className="mt-5 mb-2 font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green">Selected menu</h4>
              {detail.items?.length ? (
                <ul className="space-y-1 rounded-xl border border-nosh-border bg-white p-3">
                  {detail.items.map((it, i) => (
                    <li key={i} className="flex justify-between font-body text-sm text-nosh-ink/75">
                      <span>
                        {it.qty} × {it.name} <span className="text-nosh-ink/45">({it.size})</span>
                        {it.spice !== null && it.spice !== undefined && (
                          <span className="ml-1 font-semibold text-nosh-gold">· {spiceLabel(it.spice)}</span>
                        )}
                      </span>
                      <span>${it.price * it.qty}</span>
                    </li>
                  ))}
                  <li className="mt-1 flex justify-between border-t border-nosh-border pt-1 font-display font-bold text-nosh-green"><span>Total</span><span>${detail.estimated_total}</span></li>
                </ul>
              ) : <p className="font-body text-sm text-nosh-ink/55">No dishes selected.</p>}
              {detail.message && (<><h4 className="mt-5 mb-2 font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-green">Message</h4><p className="rounded-xl bg-nosh-surface p-3 font-body text-sm text-nosh-ink/75">{detail.message}</p></>)}
              <div className="mt-6 flex items-center justify-between gap-3">
                <a href={`mailto:${detail.email}`} className="flex-1 rounded-full bg-nosh-green px-5 py-3 text-center font-poster text-xs font-bold uppercase tracking-wide text-nosh-cream transition-colors hover:bg-nosh-ink">Reply by email</a>
                <button onClick={() => remove(detail.id)} className="rounded-full border border-red-300 px-5 py-3 font-poster text-xs font-bold uppercase tracking-wide text-red-500 transition-colors hover:bg-red-50">Delete</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
